import { Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import useStyles from "./styles";

/**
 * Gráfico de barras reutilizável com o mesmo design do dashAtendimentos
 * (grid tracejado, eixos com a tipografia do tema, barras arredondadas e
 * tooltip em "card"). Preenche 100% do container — quem usa controla a
 * altura pelo wrapper (igual aos demais gráficos do dashAtendimentos).
 *
 * Suporta:
 * - orientation: "vertical" (categorias no eixo X) | "horizontal" (no eixo Y).
 * - cor por barra: cada item pode trazer `color` (mantém o semáforo do
 *   relatório de reincidência); senão usa `defaultColor`.
 * - renderTooltipContent: conteúdo customizado do tooltip (recebe o
 *   payload[0] do recharts); senão mostra categoria + valor formatado.
 */
const BarChartComponent = ({
	data = [],
	orientation = "vertical",
	categoryKey = "label",
	valueKey = "value",
	valueFormatter = (v) => v,
	categoryFormatter,
	barSize,
	defaultColor,
	categoryWidth = 170,
	categoryAngle = 0,
	renderTooltipContent,
}) => {
	const theme = useTheme();
	const styles = useStyles(theme);

	const isHorizontal = orientation === "horizontal";
	const fallbackColor =
		defaultColor ||
		theme.palette.colors?.blue?.medium ||
		theme.palette.primary.main;
	const size = barSize ?? (isHorizontal ? 18 : 34);
	const radius = isHorizontal ? [0, 6, 6, 0] : [6, 6, 0, 0];

	const TooltipContent = ({ active, payload }) => {
		if (!active || !payload?.length) return null;
		const item = payload[0];

		return (
			<Box sx={styles.containerTooltipDonut}>
				{renderTooltipContent ? (
					renderTooltipContent(item)
				) : (
					<>
						<Typography variant="body2" sx={{ fontWeight: 600 }}>
							{item?.payload?.[categoryKey]}
						</Typography>
						<Typography variant="body2">
							{valueFormatter(item?.value)}
						</Typography>
					</>
				)}
			</Box>
		);
	};

	return (
		<ResponsiveContainer width="100%" height="100%">
			<BarChart
				data={data}
				layout={isHorizontal ? "vertical" : "horizontal"}
				margin={
					isHorizontal
						? { left: 8, right: 32, top: 4, bottom: 4 }
						: { left: 0, right: 8, top: 8, bottom: 4 }
				}
			>
				<CartesianGrid
					vertical={isHorizontal}
					horizontal={!isHorizontal}
					stroke={theme.palette.divider}
					strokeDasharray="3"
				/>

				{isHorizontal ? (
					<>
						<XAxis
							type="number"
							tickFormatter={valueFormatter}
							tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
							axisLine={false}
							tickLine={false}
						/>
						<YAxis
							type="category"
							dataKey={categoryKey}
							width={categoryWidth}
							tick={{ fontSize: 11, fill: theme.palette.text.primary }}
							axisLine={false}
							tickLine={false}
							tickFormatter={categoryFormatter}
						/>
					</>
				) : (
					<>
						<XAxis
							dataKey={categoryKey}
							tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
							axisLine={false}
							tickLine={false}
							interval={0}
							angle={categoryAngle}
							textAnchor={categoryAngle ? "end" : "middle"}
							height={categoryAngle ? 60 : 30}
							tickFormatter={categoryFormatter}
						/>
						<YAxis
							tickFormatter={valueFormatter}
							tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
							axisLine={false}
							tickLine={false}
						/>
					</>
				)}

				<Tooltip
					content={TooltipContent}
					cursor={{ fill: alpha(theme.palette.text.primary, 0.04) }}
				/>

				<Bar dataKey={valueKey} barSize={size} radius={radius}>
					{data.map((entry, i) => (
						<Cell key={i} fill={entry?.color || fallbackColor} />
					))}
				</Bar>
			</BarChart>
		</ResponsiveContainer>
	);
};

export default BarChartComponent;
